import axios from 'api';

// /api/super/reference/voices/
// Backend filterset: { scope, vendor, is_default, gender, status } (all exact).
// Search: name, voice_id, country, language_names.
// (The previous `__in` filters were vestigial — django-filter silently ignored them.)

export const getVoiceData = (filtersData, signal) => {
  const { page_size, page, search, scope, vendor, is_default, gender, status } = filtersData || {};
  const params = new URLSearchParams();
  if (page_size != null) params.set('page_size', page_size);
  if (page != null) params.set('page', page);
  if (search) params.set('search', search);
  if (scope) params.set('scope', scope);
  if (vendor != null && vendor !== '') params.set('vendor', vendor);
  if (is_default != null && is_default !== '') params.set('is_default', is_default);
  if (gender) params.set('gender', gender);
  if (status) params.set('status', status);
  return axios.get(`/api/super/reference/voices/?${params.toString()}`, { signal });
};

// Auto-detect FormData (file upload) vs JSON body. The voice endpoint accepts
// both — multipart is only needed when a `preview_file` is attached.
//
// Content-Type is explicitly cleared for multipart so the browser sets it
// together with the boundary; the axios instance defaults to
// application/json, and leaving that in place makes the backend reject the
// body. (Same trick as api/Blog's media upload.)
const isFormData = (p) => typeof FormData !== 'undefined' && p instanceof FormData;
const MULTIPART = { headers: { 'Content-Type': undefined } };

export const createVoice = (params) => {
  return axios.post(`/api/super/reference/voices/`, params, isFormData(params) ? MULTIPART : undefined);
};

export const updateVoice = (params) => {
  // Accept either a plain object {id, ...fields} or FormData carrying an `id`
  // entry. FormData has no own enumerable properties, so it cannot be
  // destructured the way the JSON branch is — read the id off it, then drop
  // it from the body since it belongs in the URL.
  if (isFormData(params)) {
    const id = params.get('id');
    params.delete('id');
    return axios.patch(`/api/super/reference/voices/${id}/`, params, MULTIPART);
  }
  const { id, ...rest } = params;
  return axios.patch(`/api/super/reference/voices/${id}/`, rest);
};

export const deleteVoice = (id) => {
  return axios.delete(`/api/super/reference/voices/${id}/`);
};

// Promote a voice to default within its TTSVendor. Any sibling voice for the
// same vendor is atomically demoted on the backend.
export const setDefaultVoice = (id) => {
  return axios.post(`/api/super/reference/voices/${id}/set-default/`);
};

export const getAllPublicVoices = () => {
  return axios.get(`/api/super/reference/voices/?page_size=1000`);
};

// /api/super/reference/voice-languages/
// Search: name. (No filterset on backend.)

export const getLanguageData = (filtersData, signal) => {
  const { page_size, page, search } = filtersData || {};
  const params = new URLSearchParams();
  if (page_size != null) params.set('page_size', page_size);
  if (page != null) params.set('page', page);
  if (search) params.set('search', search);
  return axios.get(`/api/super/reference/voice-languages/?${params.toString()}`, { signal });
};

export const createLanguage = (params) => {
  return axios.post(`/api/super/reference/voice-languages/`, params);
};

export const updateLanguage = (params) => {
  return axios.patch(`/api/super/reference/voice-languages/${params.id}/`, params);
};

export const deleteLanguage = (id) => {
  return axios.delete(`/api/super/reference/voice-languages/${id}/`);
};

export const getAllLanguages = () => {
  return axios.get(`/api/super/reference/voice-languages/?page_size=1000`);
};

// /api/super/reference/voice-accents/
// Search: name. (No filterset on backend.)

export const getAccentData = (filtersData, signal) => {
  const { page_size, page, search } = filtersData || {};
  const params = new URLSearchParams();
  if (page_size != null) params.set('page_size', page_size);
  if (page != null) params.set('page', page);
  if (search) params.set('search', search);
  return axios.get(`/api/super/reference/voice-accents/?${params.toString()}`, { signal });
};

export const createAccent = (params) => {
  return axios.post(`/api/super/reference/voice-accents/`, params);
};

export const updateAccent = (params) => {
  return axios.patch(`/api/super/reference/voice-accents/${params.id}/`, params);
};

export const deleteAccent = (id) => {
  return axios.delete(`/api/super/reference/voice-accents/${id}/`);
};

export const getAllAccents = () => {
  return axios.get(`/api/super/reference/voice-accents/?page_size=1000`);
};

// /api/super/reference/voice-styles/
// Search: name. (No filterset on backend.)

export const getStyleData = (filtersData, signal) => {
  const { page_size, page, search } = filtersData || {};
  const params = new URLSearchParams();
  if (page_size != null) params.set('page_size', page_size);
  if (page != null) params.set('page', page);
  if (search) params.set('search', search);
  return axios.get(`/api/super/reference/voice-styles/?${params.toString()}`, { signal });
};

export const createStyle = (params) => {
  return axios.post(`/api/super/reference/voice-styles/`, params);
};

export const updateStyle = (params) => {
  return axios.patch(`/api/super/reference/voice-styles/${params.id}/`, params);
};

export const deleteStyle = (id) => {
  return axios.delete(`/api/super/reference/voice-styles/${id}/`);
};

export const getAllStyles = () => {
  return axios.get(`/api/super/reference/voice-styles/?page_size=1000`);
};

// ─── Legacy stubs ────────────────────────────────────────────────────────
import { removedStub } from 'api/_legacy';
export const getCustomerVoiceData = removedStub('getCustomerVoiceData');
export const createCustomerVoice = removedStub('createCustomerVoice');
export const updateCustomerVoice = removedStub('updateCustomerVoice');
export const deleteCustomerVoice = removedStub('deleteCustomerVoice');
export const getAllTTSVendors = removedStub('getAllTTSVendors');
